import { useState, useEffect } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FiltersContainer } from './styledComponents'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getChamberRoomsWithDetails } from 'src/supportPortal/services/rosterManagementService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import ClinicRoomHierarchicalSelect from './ClinicRoomHierarchicalSelect'

const isBlank = (value) => value === null || value === undefined || String(value).trim() === ''

const normalizeStringList = (values = []) => {
  if (!Array.isArray(values)) return []
  return Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)))
}

const getRoomRosterEventTypes = (room = {}) => {
  const primaryEventTypes = normalizeStringList(room.primaryEventTypesForThisRoom)
  if (primaryEventTypes.length > 0) {
    return primaryEventTypes
  }
  return normalizeStringList(room.eventTypesForThisRoom)
}

const hasEventTypeRosteringDetails = (room) => (
  Array.isArray(room?.eventTypeRosteringDetails) &&
  room.eventTypeRosteringDetails.some((detail) => !isBlank(detail?.eventType))
)

const isRosteringDetailEnabled = (detail) => detail && !isBlank(detail.eventType) && detail.rostered !== false
const sameEventType = (first, second) => String(first).trim().toLowerCase() === String(second).trim().toLowerCase()

const isRoomRosteredForEventTypes = (room, eventTypes = []) => {
  const selectedTypes = Array.isArray(eventTypes) ? eventTypes.filter((type) => !isBlank(type)) : []
  const details = Array.isArray(room?.eventTypeRosteringDetails) ? room.eventTypeRosteringDetails : []
  const roomRosterEventTypes = getRoomRosterEventTypes(room)
  const detailEventTypes = details
    .filter(isRosteringDetailEnabled)
    .map((detail) => detail.eventType)
  const effectiveRosterEventTypes = roomRosterEventTypes.length > 0 ? roomRosterEventTypes : detailEventTypes
  const supportsSelectedEventType = selectedTypes.length === 0
    || selectedTypes.some((eventType) => effectiveRosterEventTypes.some((roomEventType) => sameEventType(roomEventType, eventType)))

  if (!supportsSelectedEventType) {
    return false
  }

  if (!hasEventTypeRosteringDetails(room)) {
    return room?.rostered === true
  }

  if (selectedTypes.length === 0) {
    return details.some((detail) => (
      isRosteringDetailEnabled(detail) &&
      effectiveRosterEventTypes.some((eventType) => sameEventType(eventType, detail.eventType))
    ))
  }

  return selectedTypes.some((eventType) => (
    details.some((detail) => (
      isRosteringDetailEnabled(detail) &&
      sameEventType(detail.eventType, eventType)
    ))
  ))
}

export default function RosterFilters({
  selectedRoomIds,
  setSelectedRoomIds,
  selectedTargetSpecialitySlugs,
  selectedEventTypes,
  allowedClinicIds,
  allowedClinics,
  isRosterAdmin,
  onRoomsDataChange, // Callback to pass full room data to parent
  onTargetSpecialitiesChange, // Callback to pass target specialities to parent
  refreshKey = 0,
}) {
  const [allRooms, setAllRooms] = useState([])
  const [targetSpecialities, setTargetSpecialities] = useState([])
  const [loading, setLoading] = useState(false)

  // Fetch all rooms upfront (filtered by allowed clinics for managers and by Target Speciality/Event Type filters)
  useEffect(() => {
    async function fetchAllRooms() {
      setLoading(true)
      try {
        // For managers, filter by allowed clinic brand IDs
        // For admins, fetch all rooms
        const clinicBrandIds = isRosterAdmin ? null : allowedClinicIds

        // Combine targetSpecialitySlugs from:
        // 1. User-selected filters (selectedTargetSpecialitySlugs)
        // 2. Allowed clinics for managers (if no user selection)
        let targetSpecialitySlugs = selectedTargetSpecialitySlugs && selectedTargetSpecialitySlugs.length > 0 
          ? selectedTargetSpecialitySlugs 
          : null

        // If no user selection and manager, use allowed clinics' targetSpecialitySlugs
        if (!targetSpecialitySlugs && !isRosterAdmin && allowedClinics && allowedClinics.length > 0) {
          // Collect all unique targetSpecialitySlugs from allowed clinics
          const allSlugs = new Set()
          allowedClinics.forEach((clinic) => {
            if (clinic.targetSpecialitySlugs && Array.isArray(clinic.targetSpecialitySlugs)) {
              clinic.targetSpecialitySlugs.forEach((slug) => allSlugs.add(slug))
            }
          })
          // Only use targetSpecialitySlugs if at least one clinic has them specified
          // If all clinics have empty/missing targetSpecialitySlugs, it means all specialities allowed
          if (allSlugs.size > 0) {
            targetSpecialitySlugs = Array.from(allSlugs)
          }
        }

        const rosterEventTypes = selectedEventTypes && selectedEventTypes.length > 0
          ? selectedEventTypes 
          : null

        const resp = await getChamberRoomsWithDetails({
          clinicBrandIds,
          targetSpecialitySlugs, // Filter by target speciality slugs (user selection or allowed clinics)
          preferredEventTypes: rosterEventTypes,
          rostered: true, // Explicitly request only rostered rooms
          includeAssociatedMentors: true, // Include mentors filtered by RosterSchedule.eventTypes
        })

        if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)?.['chamber-room'] || []
          
          // Additional client-side filter as safety check (in case API doesn't filter)
          const rosteredRooms = data.filter((room) => isRoomRosteredForEventTypes(room, selectedEventTypes))
          setAllRooms(rosteredRooms)
          
          // Pass full room data to parent component for accessing mentorsAndAuditors
          if (onRoomsDataChange) {
            onRoomsDataChange(rosteredRooms)
          }
        }
      } catch (err) {
        console.error('Error fetching rooms:', err)
      } finally {
        setLoading(false)
      }
    }

    // Only fetch if admin or if manager has allowed clinics
    if (isRosterAdmin || (allowedClinicIds && allowedClinicIds.length > 0)) {
      fetchAllRooms()
    } else if (!isRosterAdmin && (!allowedClinicIds || allowedClinicIds.length === 0)) {
      // Manager with no allowed clinics
      setAllRooms([])
      setSelectedRoomIds([]) // Clear selected rooms when no clinics available
      if (onRoomsDataChange) {
        onRoomsDataChange([])
      }
    }
  }, [
    isRosterAdmin,
    allowedClinicIds,
    allowedClinics,
    selectedTargetSpecialitySlugs,
    selectedEventTypes,
    refreshKey,
    setSelectedRoomIds,
    onRoomsDataChange,
  ])

  // Fetch target specialties
  useEffect(() => {
    async function fetchTargetSpecialities() {
      setLoading(true)
      try {
        const resp = await getSPDataWithParams('/api/secure/cc/support/target-specialties/page', {
          includeDisabled: false,
          page: 0,
          size: 200,
        })
        
        if (resp.status === 200) {
          let specialties = []
          
          // Handle different response structures
          // Check if resp.data is directly an array (non-CommonResponse format)
          if (Array.isArray(resp.data)) {
            specialties = resp.data
          } else if (isCommonResponseSuccessfulV2(resp)) {
            // CommonResponse format: { code: 200, data: {...} }
            // getDataMapFromCommonResponse returns resp?.data?.data
            const data = getDataMapFromCommonResponse(resp)
            
            // Handle different nested structures:
            // 1. Paginated response: { content: [...] }
            // 2. Direct array in data.data: [...]
            // 3. Direct array in data: [...]
            // 4. Object with targetSpecialities key: { targetSpecialities: [...] }
            if (Array.isArray(data)) {
              // Array from getDataMapFromCommonResponse
              specialties = data
            } else if (data?.content && Array.isArray(data.content)) {
              // Paginated response with content property
              specialties = data.content
            } else if (data?.targetSpecialities && Array.isArray(data.targetSpecialities)) {
              // Object with targetSpecialities key
              specialties = data.targetSpecialities
            } else if (data && typeof data === 'object') {
              // Try to find any array property
              const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]))
              if (arrayKey) {
                specialties = data[arrayKey]
              }
            }
            
            // Fallback: check resp.data directly if getDataMapFromCommonResponse didn't work
            if (specialties.length === 0 && resp.data && typeof resp.data === 'object') {
              if (Array.isArray(resp.data)) {
                specialties = resp.data
              } else if (resp.data?.content && Array.isArray(resp.data.content)) {
                specialties = resp.data.content
              } else if (resp.data?.data && Array.isArray(resp.data.data)) {
                specialties = resp.data.data
              }
            }
          } else {
            // Not CommonResponse format - check nested structures
            if (resp.data?.content && Array.isArray(resp.data.content)) {
              specialties = resp.data.content
            } else if (resp.data?.data && Array.isArray(resp.data.data)) {
              specialties = resp.data.data
            }
          }
          
          setTargetSpecialities(specialties)
        } else {
          console.error('Failed to fetch target specialties:', resp)
        }
      } catch (err) {
        console.error('Error fetching target specialties:', err)
      } finally {
        setLoading(false)
      }
    }

      fetchTargetSpecialities()
    }, [])

  // Pass target specialities to parent when they change
  useEffect(() => {
    if (onTargetSpecialitiesChange) {
      onTargetSpecialitiesChange(targetSpecialities)
    }
  }, [targetSpecialities, onTargetSpecialitiesChange])

  // Filter selectedRoomIds when allRooms changes (e.g., when filters are applied)
  // This ensures selected rooms are cleared if they no longer match the filters
  useEffect(() => {
    if (selectedRoomIds && selectedRoomIds.length > 0 && allRooms.length > 0) {
      const validRoomIds = allRooms.map(room => room.id)
      const filteredSelectedIds = selectedRoomIds.filter(id => validRoomIds.includes(id))
      if (filteredSelectedIds.length !== selectedRoomIds.length) {
        setSelectedRoomIds(filteredSelectedIds)
      }
    } else if (allRooms.length === 0 && selectedRoomIds && selectedRoomIds.length > 0) {
      // If no rooms available, clear selection
      setSelectedRoomIds([])
    }
  }, [allRooms, selectedRoomIds, setSelectedRoomIds])

  const handleRoomChange = (selectedRoomIds) => {
    setSelectedRoomIds(selectedRoomIds)
  }

  const renderFilterWithClear = (label, value, onClear, children) => {
    const hasValue = Array.isArray(value) ? value.length > 0 : value !== null && value !== ''
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {children}
        {hasValue && (
          <IconButton
            size="small"
            onClick={onClear}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main', backgroundColor: 'action.hover' },
            }}
            title={`Clear ${label}`}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    )
  }

  return (
    <FiltersContainer>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            Filter
          </Typography>
        {renderFilterWithClear(
          'Clinic & Room',
          selectedRoomIds,
          () => setSelectedRoomIds([]),
          <ClinicRoomHierarchicalSelect
            rooms={allRooms}
            selectedRoomIds={selectedRoomIds}
            onChange={handleRoomChange}
            label="Clinic & Room"
            disabled={loading || allRooms.length === 0}
            multiple={true}
          />,
        )}
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {loading ? 'Loading rooms...' : `${allRooms.length} rostered room${allRooms.length === 1 ? '' : 's'}`}
        </Typography>
      </Stack>
    </FiltersContainer>
  )
}
